import React, { useCallback, useEffect, useRef, useState } from "react";
import Loader from "../../../components/loader";
import SnackbarAlert from "../../../components/SnackBarAlert";
import { classSimulationPath } from "../../../utils/self.serve.enrolment-storage";
import ClassAccessSimulationCard from "./ClassAccessSimulationCard";
import ClassPublicShell from "./ClassPublicShell";
import ClassUnavailableNotice from "./ClassUnavailableNotice";
import { CLOSED_ALERT, SsAccessSimulationLocationState, SsAlertState } from "./self.serve.alert";
import { useEnrolledSimulations } from "./useEnrolledSimulations";

/** Success message shown once, right after a redeem from the landing page. */
const REDEEM_SUCCESS_MESSAGE = "You are enrolled. Your simulations are listed below.";

/** Ignore a second View click for the same sim while the first tab is still opening. */
const VIEW_OPEN_GUARD_MS = 1000;

/**
 * Public Access Simulation — `/class/:slug/access-simulation` (ELS-641).
 * Requires a visit-scoped enrolment JWT in sessionStorage for this slug.
 * A later redeem in another tab for the same class removes this tab's JWT.
 * Simulations are paged by the backend and the next page loads as the student scrolls.
 * The redeem success message and sim-list load errors appear in the shared alert box.
 */

const ClassLandingAccessSimulation: React.FC<any> = (props) => {
  const slug = props.match?.params?.slug;
  const [alert, setAlert] = useState<SsAlertState>(CLOSED_ALERT);
  /** Sim id last opened via View; blocks a double-click from opening two tabs for that sim. */
  const openingSimIdRef = useRef<string | null>(null);

  /** Back to the class landing page (replacing history), e.g. when the session is no longer valid. */
  const goToLanding = useCallback(() => {
    if (!slug) {
      return;
    }
    props.history.replace(`/class/${encodeURIComponent(slug)}`);
  }, [slug, props.history]);

  /** Shows a sim-list load error in the alert box. Stable, because the hook's load effect depends on it. */
  const showError = useCallback((msg: string) => {
    setAlert({ open: true, type: "error", msg });
  }, []);

  // Right after a redeem, show the success message once, then drop the flag from history so a
  // refresh or Back does not show it again.
  useEffect(() => {
    const state = props.location?.state as SsAccessSimulationLocationState | undefined;
    if (state?.redeemed) {
      setAlert({ open: true, type: "success", msg: REDEEM_SUCCESS_MESSAGE });
      props.history.replace(props.location.pathname);
    }
    // Run once on mount; the replace above changes location, which must not re-trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { loading, loadingMore, error, className, studentName, simulations, hasMore, sentinelRef } =
    useEnrolledSimulations({
      slug,
      onSessionExpired: goToLanding,
      onError: showError,
    });

  return (
    <ClassPublicShell title={className} titleAlign="center">
      {studentName && !error ? (
        <p className="ss-enrolled-student">
          Enrolled as: {studentName}
        </p>
      ) : null}
      {error ? (
        <div className="ss-public-card">
          <ClassUnavailableNotice message={error} />
        </div>
      ) : simulations.length === 0 && !loading ? (
        <div className="ss-public-empty" role="status">
          No simulations are assigned to this class yet.
        </div>
      ) : (
        <>
          <div className="ss-sim-grid" role="list" aria-label="Assigned simulations">
            {simulations.map((sim) => (
              <ClassAccessSimulationCard
                key={sim.simId}
                simId={sim.simId}
                label={sim.label}
                onView={(id) => {
                  if (!slug) {
                    return;
                  }
                  // Double View on the same sim would open two tabs for it.
                  if (openingSimIdRef.current === id) {
                    return;
                  }
                  openingSimIdRef.current = id;
                  // Same-origin window.open copies this tab's sessionStorage, so the new tab
                  // keeps the enrolment JWT. noopener would drop that copy.
                  const opened = window.open(classSimulationPath(slug, id), "_blank");
                  if (!opened) {
                    openingSimIdRef.current = null;
                    showError("Allow pop-ups to open the simulation in a new tab.");
                    return;
                  }
                  window.setTimeout(() => {
                    if (openingSimIdRef.current === id) {
                      openingSimIdRef.current = null;
                    }
                  }, VIEW_OPEN_GUARD_MS);
                }}
              />
            ))}
          </div>
          {/* Scrolling this into view loads the next backend page (see useEnrolledSimulations). */}
          {hasMore ? (
            <div ref={sentinelRef} className="ss-sim-scroll-sentinel" aria-hidden="true" />
          ) : null}
          {loadingMore ? (
            <div className="ss-sim-loading-more" role="status">
              Loading more simulations…
            </div>
          ) : null}
        </>
      )}
      {loading ? (
        <div className="overlay" role="status" aria-live="polite" aria-label="Loading">
          <Loader loading={true} />
        </div>
      ) : null}
      {/* Redeem success and sim-list load errors. */}
      <SnackbarAlert
        openAlert={alert.open}
        setOpenAlert={() => setAlert(CLOSED_ALERT)}
        type={alert.type}
        msg={alert.msg}
      />
    </ClassPublicShell>
  );
};

export default ClassLandingAccessSimulation;
